import type { LibraryTemplate } from "../library";
import {
  BLACK,
  captions,
  cornerLogo,
  during,
  ellipse,
  enter,
  logo,
  ON_VIDEO,
  parts,
  pill,
  REEL_CAPTIONS,
  rect,
  stack,
  star,
  TEXT_SHADOW,
  t,
  text,
  WHITE,
} from "./shorthands";

/** Reels, TikTok und Shorts (9:16). */

const FIRST_REELS: LibraryTemplate[] = [
  {
    id: "reel-titel-untertitel",
    title: "Reel: Titel & Untertitel",
    description: "Titel-Box oben, animierte Untertitel in der Marke, Logo darunter",
    presetId: "reel",
    topic: "wissen",
    style: "laut",
    pages: [
      {
        background: "#000000",
        nodes: [
          rect({
            x: 70,
            y: 200,
            w: 940,
            h: 250,
            fill: "accent",
            radius: "md",
            name: "Titel-Box",
            animation: { in: { preset: "pop", duration: 500 } },
          }),
          text({
            text: "3 Dinge, die du über uns noch nicht wusstest",
            style: "heading-1",
            size: 76,
            color: "on:accent",
            x: 110,
            y: 200,
            w: 860,
            h: 250,
            align: "center",
            animation: { in: { preset: "pop", duration: 500 } },
          }),
          captions(REEL_CAPTIONS),
          logo({ x: 780, y: 470, w: 230, h: 64, on: "#000000", align: "right" }),
        ],
      },
    ],
  },
  {
    id: "reel-intro",
    title: "Reel: Intro-Karte",
    description: "Farbige Titelkarte in den ersten Sekunden, danach Untertitel",
    presetId: "reel",
    topic: "ankuendigung",
    style: "verspielt",
    pages: [
      {
        background: "#000000",
        nodes: [
          logo({ x: 70, y: 110, w: 200, h: 70, on: "#000000" }),
          captions(REEL_CAPTIONS),
          rect({
            x: 0,
            y: 0,
            w: 1080,
            h: 1920,
            fill: "primary",
            name: "Intro",
            time: { start: 0, end: 2600 },
            animation: { out: { preset: "fade", duration: 400 } },
          }),
          ellipse({
            x: 620,
            y: -160,
            w: 620,
            h: 620,
            fill: "accent",
            name: "Kreis",
            time: { start: 0, end: 2600 },
            animation: {
              in: { preset: "zoom", duration: 600 },
              out: { preset: "fade", duration: 400 },
            },
          }),
          stack({
            x: 90,
            y: 960,
            w: 900,
            anchor: "center",
            gap: 24,
            items: [
              t("FOLGE 1", "subline", 44, "on:primary/80", {
                time: { start: 0, end: 2600 },
                animation: {
                  in: { preset: "slide-up", duration: 500 },
                  out: { preset: "fade", duration: 400 },
                },
              }),
              t("Hinter den Kulissen", "heading-1", 140, "on:primary", {
                time: { start: 0, end: 2600 },
                animation: {
                  in: { preset: "slide-up", duration: 600 },
                  out: { preset: "fade", duration: 400 },
                },
              }),
            ],
          }),
          logo({
            x: 340,
            y: 1650,
            w: 400,
            h: 110,
            on: "primary",
            align: "center",
            time: { start: 0, end: 2600 },
            animation: {
              in: { preset: "fade", duration: 600 },
              out: { preset: "fade", duration: 400 },
            },
          }),
        ],
      },
    ],
  },
  {
    id: "reel-tipp",
    title: "Reel: Tipp-Einblendung",
    description: "Helle Karte mit Tipp-Nummer und Frage, darunter Untertitel",
    presetId: "reel",
    topic: "wissen",
    style: "klar",
    pages: [
      {
        background: "#000000",
        nodes: [
          rect({
            x: 60,
            y: 170,
            w: 960,
            h: 330,
            fill: "background/92",
            radius: "lg",
            name: "Karte",
            animation: { in: { preset: "slide-down", duration: 500 } },
          }),
          ...pill(100, 210, 240, 64, "TIPP #1", "primary", 28, {
            animation: { in: { preset: "slide-down", duration: 500 } },
          }),
          text({
            text: "So schneidest du Videos direkt am Handy",
            style: "heading-2",
            size: 60,
            color: "text",
            x: 100,
            y: 300,
            w: 880,
            h: 170,
            animation: { in: { preset: "slide-down", duration: 500 } },
          }),
          captions(REEL_CAPTIONS),
          logo({ x: 760, y: 210, w: 220, h: 64, on: "background", align: "right" }),
        ],
      },
    ],
  },
];

const MORE_REELS: LibraryTemplate[] = [
  {
    id: "reel-hook",
    title: "Reel: Hook & Untertitel",
    description: "Großer Hook für die ersten Sekunden, Themen-Chip oben und Untertitel",
    presetId: "reel",
    topic: "wissen",
    style: "nacht",
    tags: ["talking head", "hook", "sprechen", "kamera", "selfie", "untertitel"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 440, 72, "SELBSTSTÄNDIG · TEIL 1", "primary", 26, enter("slide-down")),
          cornerLogo(),
          rect({
            x: 80,
            y: 350,
            w: 920,
            h: 320,
            fill: "accent",
            radius: "md",
            rotation: -2,
            name: "Hook-Fläche",
            ...during(0, 3500, "pop", { duration: 450 }),
          }),
          text({
            text: "Das hat mir niemand über Selbstständigkeit gesagt",
            style: "heading-1",
            size: 80,
            color: "on:accent",
            x: 130,
            y: 350,
            w: 820,
            h: 320,
            align: "center",
            rotation: -2,
            name: "Hook",
            ...during(0, 3500, "pop", { duration: 450 }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-3-tipps",
    title: "Reel: 3 Tipps nacheinander",
    description: "Thema oben, dann erscheinen Tipp 1, 2 und 3 nacheinander – mit Untertiteln",
    presetId: "reel",
    topic: "wissen",
    style: "klar",
    tags: ["tipps", "liste", "nummeriert", "ratgeber", "how-to", "schritte"],
    previewTime: 2000,
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 240,
            w: 960,
            h: 190,
            fill: "background/92",
            radius: "lg",
            name: "Titel-Karte",
            ...enter("slide-down"),
          }),
          logo({ x: 100, y: 262, w: 200, h: 44, on: "background", ...enter("slide-down") }),
          text({
            text: "3 Tipps für bessere Handyvideos",
            style: "heading-2",
            size: 54,
            color: "text",
            x: 100,
            y: 314,
            w: 880,
            h: 96,
            ...enter("slide-down"),
          }),
          ...parts(3, 1200, 5500).flatMap(({ index, start, end }) => {
            const tips = ["Licht von vorne", "Handy auf Augenhöhe", "Ton vor Bild"];
            return [
              ellipse({
                x: 70,
                y: 460,
                w: 150,
                h: 150,
                fill: "primary",
                name: `Nummer ${index + 1}`,
                ...during(start, end, "pop", { duration: 400 }),
              }),
              text({
                text: String(index + 1),
                style: "heading-1",
                size: 92,
                color: "on:primary",
                x: 70,
                y: 460,
                w: 150,
                h: 150,
                align: "center",
                ...during(start, end, "pop", { duration: 400 }),
              }),
              rect({
                x: 240,
                y: 475,
                w: 700,
                h: 120,
                fill: "background/92",
                radius: "md",
                name: `Tipp ${index + 1}`,
                ...during(start, end, "slide-left", { duration: 450 }),
              }),
              text({
                text: tips[index]!,
                style: "heading-2",
                size: 48,
                color: "text",
                x: 275,
                y: 475,
                w: 640,
                h: 120,
                ...during(start, end, "slide-left", { duration: 450 }),
              }),
            ];
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-produkt",
    title: "Reel: Produktvorstellung",
    description: "Produktname, Preis-Sticker und pulsierender Button zum Shop",
    presetId: "reel",
    topic: "produkt",
    style: "verspielt",
    tags: ["produkt", "shop", "preis", "e-commerce", "verkaufen", "neuheit", "online-shop"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 330, 70, "NEU IM SHOP", "accent", 28, enter("slide-down")),
          cornerLogo(),
          stack({
            x: 70,
            y: 350,
            w: 860,
            gap: 16,
            items: [
              t("Keramikbecher „Morgenrot“", "heading-1", 96, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("slide-up", { duration: 600 }),
              }),
              t("Handgemacht in Leipzig · spülmaschinenfest", "body", 36, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("slide-up", { duration: 800 }),
              }),
            ],
          }),
          // Button with the price sticker on its end: they move as one in other formats.
          ...pill(140, 1270, 580, 110, "Jetzt im Shop", "primary", 40, {
            ...enter("pop", { duration: 1200, loop: "pulse" }),
          }),
          star({
            x: 650,
            y: 1150,
            w: 240,
            h: 240,
            sides: 14,
            innerRatio: 0.82,
            rotation: 8,
            fill: "accent",
            name: "Preis-Sticker",
            ...enter("pop", { duration: 900, loop: "wiggle" }),
          }),
          text({
            text: "29,90\u00a0€",
            style: "heading-1",
            size: 64,
            color: "on:accent",
            x: 680,
            y: 1150,
            w: 180,
            h: 240,
            rotation: 8,
            align: "center",
            ...enter("pop", { duration: 900, loop: "wiggle" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-rezept",
    title: "Reel: Rezept in Schritten",
    description: "Rezept-Titel oben, Schritt 1 bis 3 nacheinander, dazu Untertitel",
    presetId: "reel",
    topic: "wissen",
    style: "natur",
    tags: ["rezept", "kochen", "backen", "anleitung", "schritte", "gastro", "food", "how-to"],
    previewTime: 1500,
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 240,
            w: 960,
            h: 196,
            fill: "background/92",
            radius: "lg",
            name: "Titel-Karte",
            ...enter("slide-down"),
          }),
          text({
            text: "REZEPT · 15 MINUTEN",
            style: "subline",
            size: 28,
            color: "primary",
            x: 100,
            y: 264,
            w: 620,
            h: 44,
            ...enter("slide-down"),
          }),
          logo({
            x: 790,
            y: 264,
            w: 190,
            h: 44,
            on: "background",
            align: "right",
            ...enter("slide-down"),
          }),
          text({
            text: "Kürbissuppe mit Ingwer",
            style: "heading-2",
            size: 60,
            color: "text",
            x: 100,
            y: 316,
            w: 880,
            h: 96,
            ...enter("slide-down"),
          }),
          ...parts(3, 1000, 6000).flatMap(({ index, start, end }) => {
            const steps = [
              "Kürbis und Zwiebel würfeln, in Butter anschwitzen",
              "Mit Brühe ablöschen und 10 Minuten köcheln",
              "Ingwer dazu, fein pürieren – fertig!",
            ];
            return [
              ...pill(60, 466, 250, 64, `SCHRITT ${index + 1}`, "accent", 26, {
                ...during(start, end, "slide-right", { duration: 400 }),
              }),
              text({
                text: steps[index]!,
                style: "heading-2",
                size: 50,
                color: WHITE,
                shadow: TEXT_SHADOW,
                x: 60,
                y: 550,
                w: 860,
                name: `Schritt ${index + 1}`,
                ...during(start, end, "slide-up", { duration: 500 }),
              }),
            ];
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-vorher-nachher",
    title: "Reel: Vorher / Nachher",
    description: "„Vorher“ am Anfang, ein Blitz und dann „Nachher“ – ideal für Ergebnisse",
    presetId: "reel",
    topic: "produkt",
    style: "warm",
    tags: [
      "vorher nachher",
      "transformation",
      "ergebnis",
      "handwerk",
      "renovierung",
      "friseur",
      "reinigung",
    ],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 300, 84, "VORHER", "secondary", 34, {
            ...during(0, 4000, "slide-right", { duration: 400, out: null }),
          }),
          ...pill(70, 250, 340, 84, "NACHHER", "primary", 34, {
            ...during(4000, 15000, "pop", { duration: 400 }),
          }),
          cornerLogo(),
          stack({
            x: 70,
            y: 1400,
            w: 860,
            anchor: "bottom",
            gap: 12,
            items: [
              t("Badsanierung in 3 Tagen", "heading-1", 76, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("slide-up", { duration: 600 }),
              }),
              t("Meisterbetrieb Müller · Köln", "subline", 32, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("slide-up", { duration: 800 }),
              }),
            ],
          }),
          rect({
            x: 0,
            y: 0,
            w: 1080,
            h: 1920,
            fill: "#ffffffe6",
            name: "Blitz",
            ...during(3800, 4300, "fade", { duration: 150, outDuration: 300 }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-hinter-den-kulissen",
    title: "Reel: Hinter den Kulissen",
    description: "Kamera-Sucher mit blinkendem Aufnahmepunkt, Ort und dezente Untertitel",
    presetId: "reel",
    topic: "team",
    style: "traditionell",
    tags: ["behind the scenes", "bts", "einblick", "werkstatt", "alltag", "team", "making-of"],
    pages: [
      {
        background: BLACK,
        nodes: [
          // Viewfinder corners, clear of the texts so they stay in the corners in other formats.
          ...[
            [60, 230, 70, 8],
            [60, 230, 8, 70],
            [950, 230, 70, 8],
            [1012, 230, 8, 70],
            [60, 1432, 70, 8],
            [60, 1370, 8, 70],
            [950, 1432, 70, 8],
            [1012, 1370, 8, 70],
          ].map(([x, y, w, h]) =>
            rect({ x: x!, y: y!, w: w!, h: h!, fill: "#ffffffd9", name: "Sucher" }),
          ),
          ellipse({
            x: 110,
            y: 350,
            w: 32,
            h: 32,
            fill: "#ef4444",
            name: "Aufnahme",
            ...enter("fade", { loop: "pulse" }),
          }),
          text({
            text: "HINTER DEN KULISSEN",
            style: "subline",
            size: 30,
            x: 160,
            y: 340,
            w: 520,
            h: 52,
            ...ON_VIDEO,
            ...enter("fade"),
          }),
          logo({ x: 700, y: 336, w: 220, h: 60, on: BLACK, align: "right" }),
          text({
            text: "Tag 1 · In der Werkstatt",
            style: "heading-1",
            size: 72,
            x: 110,
            y: 410,
            w: 810,
            ...ON_VIDEO,
            ...enter("slide-up", { duration: 700 }),
          }),
          captions({ ...REEL_CAPTIONS, look: "minimal" }),
        ],
      },
    ],
  },
  {
    id: "reel-kundenstimme",
    title: "Reel: Kundenstimme",
    description: "Zitat-Karte mit fünf Sternen, Name als Bauchbinde und Untertitel",
    presetId: "reel",
    topic: "zitat",
    style: "elegant",
    tags: ["testimonial", "bewertung", "rezension", "sterne", "review", "referenz", "feedback"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 240,
            w: 960,
            h: 400,
            fill: "background/94",
            radius: "lg",
            name: "Zitat-Karte",
            ...enter("slide-down", { duration: 600 }),
          }),
          ...[0, 1, 2, 3, 4].map((index) =>
            star({
              x: 100 + index * 70,
              y: 280,
              w: 56,
              h: 56,
              sides: 5,
              innerRatio: 0.45,
              fill: "accent",
              name: "Stern",
              ...enter("pop", { duration: 500 + index * 180 }),
            }),
          ),
          logo({ x: 760, y: 278, w: 220, h: 60, on: "background", align: "right" }),
          text({
            text: "„Endlich eine Agentur, die zuhört – und liefert.“",
            style: "quote",
            size: 60,
            color: "text",
            x: 100,
            y: 370,
            w: 880,
            h: 240,
            ...enter("fade", { duration: 900 }),
          }),
          rect({
            x: 60,
            y: 668,
            w: 640,
            h: 130,
            fill: "primary",
            radius: "md",
            name: "Name",
            ...enter("slide-right", { duration: 800 }),
          }),
          stack({
            x: 95,
            y: 733,
            w: 580,
            anchor: "center",
            gap: 4,
            items: [
              t(
                "Sandra Keller",
                "heading-2",
                44,
                "on:primary",
                enter("slide-right", { duration: 800 }),
              ),
              t(
                "Inhaberin, Café Keller",
                "caption",
                28,
                "on:primary/80",
                enter("slide-right", { duration: 800 }),
              ),
            ],
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-event",
    title: "Reel: Event-Ankündigung",
    description: "Datum als Badge, Titel, Ort und ein pulsierender Ticket-Hinweis",
    presetId: "reel",
    topic: "event",
    style: "laut",
    tags: ["event", "veranstaltung", "termin", "datum", "tickets", "konzert", "party", "fest"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 70,
            y: 250,
            w: 220,
            h: 240,
            fill: "accent",
            radius: "md",
            name: "Datum",
            ...enter("pop", { duration: 600 }),
          }),
          text({
            text: "24",
            style: "heading-1",
            size: 124,
            color: "on:accent",
            x: 70,
            y: 262,
            w: 220,
            h: 140,
            align: "center",
            ...enter("pop", { duration: 600 }),
          }),
          text({
            text: "OKT",
            style: "subline",
            size: 40,
            color: "on:accent",
            x: 70,
            y: 400,
            w: 220,
            h: 60,
            align: "center",
            ...enter("pop", { duration: 600 }),
          }),
          stack({
            x: 320,
            y: 370,
            w: 600,
            anchor: "center",
            gap: 10,
            items: [
              t("SOMMERFEST", "subline", 32, "accent", {
                shadow: TEXT_SHADOW,
                ...enter("slide-left", { duration: 600 }),
              }),
              t("Live-Musik im Hofgarten", "heading-1", 84, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("slide-left", { duration: 700 }),
              }),
            ],
          }),
          text({
            text: "Samstag · 18 Uhr · Hofgarten 12, Köln",
            style: "body",
            size: 36,
            x: 70,
            y: 522,
            w: 860,
            ...ON_VIDEO,
            ...enter("fade", { duration: 800 }),
          }),
          ...pill(70, 610, 500, 90, "Tickets: Link in der Bio", "primary", 30, {
            ...enter("pop", { duration: 1000, loop: "pulse" }),
          }),
          logo({ x: 600, y: 622, w: 320, h: 66, on: BLACK, align: "right" }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-wir-stellen-ein",
    title: "Reel: Wir stellen ein",
    description: "Schräges Banner, Stellen-Karte mit Vorteilen und „Jetzt bewerben“",
    presetId: "reel",
    topic: "team",
    style: "verspielt",
    tags: [
      "job",
      "stellenanzeige",
      "karriere",
      "recruiting",
      "bewerbung",
      "ausbildung",
      "personal",
    ],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: -40,
            y: 260,
            w: 1160,
            h: 170,
            fill: "accent",
            rotation: -4,
            name: "Banner",
            ...enter("fly-left", { duration: 600 }),
          }),
          text({
            text: "Wir stellen ein!",
            style: "heading-1",
            size: 100,
            color: "on:accent",
            x: 60,
            y: 260,
            w: 960,
            h: 170,
            align: "center",
            rotation: -4,
            ...enter("fly-left", { duration: 600 }),
          }),
          rect({
            x: 60,
            y: 480,
            w: 960,
            h: 290,
            fill: "background/94",
            radius: "lg",
            name: "Stelle",
            ...enter("slide-up", { duration: 600 }),
          }),
          text({
            text: "VOLLZEIT · KÖLN · AB SOFORT",
            style: "subline",
            size: 26,
            color: "primary",
            x: 100,
            y: 506,
            w: 640,
            h: 44,
            ...enter("slide-up", { duration: 600 }),
          }),
          logo({
            x: 770,
            y: 506,
            w: 210,
            h: 44,
            on: "background",
            align: "right",
            ...enter("slide-up", { duration: 600 }),
          }),
          text({
            text: "Mediengestalter:in Digital & Print",
            style: "heading-2",
            size: 56,
            color: "text",
            x: 100,
            y: 556,
            w: 880,
            h: 130,
            ...enter("slide-up", { duration: 600 }),
          }),
          text({
            text: "4-Tage-Woche · Jobrad · Weiterbildung",
            style: "body",
            size: 30,
            color: "text-muted",
            x: 100,
            y: 700,
            w: 880,
            h: 46,
            ...enter("slide-up", { duration: 600 }),
          }),
          ...pill(60, 800, 500, 84, "Jetzt bewerben", "primary", 32, {
            ...enter("pop", { duration: 1200, loop: "pulse" }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-immobilie",
    title: "Reel: Objekt-Rundgang",
    description: "Immobilie mit Lage, drei Eckdaten, Preis und Untertiteln für den Rundgang",
    presetId: "reel",
    topic: "produkt",
    style: "elegant",
    tags: ["immobilie", "makler", "wohnung", "haus", "exposé", "rundgang", "vermietung", "kaufen"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 360, 70, "NEU IM ANGEBOT", "accent", 26, enter("slide-down")),
          cornerLogo(),
          text({
            text: "Altbauwohnung mit Balkon",
            style: "heading-1",
            size: 70,
            x: 70,
            y: 340,
            w: 860,
            h: 164,
            ...ON_VIDEO,
            ...enter("slide-up", { duration: 600 }),
          }),
          text({
            text: "KÖLN-EHRENFELD",
            style: "subline",
            size: 30,
            x: 70,
            y: 508,
            w: 860,
            h: 44,
            ...ON_VIDEO,
            ...enter("slide-up", { duration: 700 }),
          }),
          ...(
            [
              ["4", "Zimmer"],
              ["112\u00a0m²", "Wohnfläche"],
              ["Süd", "Balkon"],
            ] as const
          ).flatMap(([value, label], index) => {
            const x = 70 + index * 290;
            const motion = enter("pop", { duration: 500 + index * 200 });
            return [
              rect({
                x,
                y: 590,
                w: 270,
                h: 136,
                fill: "background/92",
                radius: "md",
                name: label,
                ...motion,
              }),
              text({
                text: value,
                style: "heading-2",
                size: 54,
                color: "text",
                x: x + 15,
                y: 598,
                w: 240,
                h: 76,
                align: "center",
                ...motion,
              }),
              text({
                text: label,
                style: "caption",
                size: 26,
                color: "text-muted",
                x: x + 15,
                y: 676,
                w: 240,
                h: 40,
                align: "center",
                ...motion,
              }),
            ];
          }),
          ...pill(70, 752, 420, 84, "489.000\u00a0€", "primary", 40, {
            radius: "md",
            ...enter("slide-right", { duration: 900 }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-tagesgericht",
    title: "Reel: Tagesgericht",
    description: "Karte mit Gericht und Preis-Kreis, Uhrzeiten und Reservieren-Button",
    presetId: "reel",
    topic: "angebot",
    style: "warm",
    tags: [
      "restaurant",
      "café",
      "mittagstisch",
      "gastro",
      "speisekarte",
      "essen",
      "imbiss",
      "menü",
    ],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 240,
            w: 960,
            h: 330,
            fill: "background/94",
            radius: "lg",
            name: "Karte",
            ...enter("slide-down", { duration: 600 }),
          }),
          ...pill(
            100,
            275,
            420,
            60,
            "HEUTE AUF DER KARTE",
            "primary",
            24,
            enter("slide-down", { duration: 600 }),
          ),
          logo({
            x: 760,
            y: 275,
            w: 220,
            h: 60,
            on: "background",
            align: "right",
            ...enter("slide-down", { duration: 600 }),
          }),
          text({
            text: "Hausgemachte Gnocchi mit Salbeibutter",
            style: "heading-2",
            size: 58,
            color: "text",
            x: 100,
            y: 360,
            w: 660,
            h: 180,
            ...enter("slide-down", { duration: 600 }),
          }),
          ellipse({
            x: 790,
            y: 400,
            w: 210,
            h: 210,
            fill: "accent",
            rotation: -8,
            name: "Preis",
            ...enter("pop", { duration: 900, loop: "wiggle" }),
          }),
          text({
            text: "12,90\u00a0€",
            style: "heading-2",
            size: 50,
            color: "on:accent",
            x: 805,
            y: 400,
            w: 180,
            h: 210,
            rotation: -8,
            align: "center",
            ...enter("pop", { duration: 900, loop: "wiggle" }),
          }),
          text({
            text: "Mo – Fr · 11:30 bis 14:30 Uhr",
            style: "subline",
            size: 32,
            x: 70,
            y: 640,
            w: 700,
            ...ON_VIDEO,
            ...enter("fade", { duration: 900 }),
          }),
          ...pill(260, 1260, 560, 110, "Jetzt reservieren", "primary", 38, {
            ...enter("pop", { duration: 1200, loop: "pulse" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-community-frage",
    title: "Reel: Frage aus der Community",
    description: "Sprechblase mit der Frage eines Followers – die Antwort kommt als Untertitel",
    presetId: "reel",
    topic: "community",
    style: "klar",
    tags: ["frage", "faq", "antwort", "kommentar", "q&a", "follower", "community"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 250,
            w: 900,
            h: 300,
            fill: "background",
            radius: "lg",
            shadow: { y: 12, blur: 40, color: "#00000059" },
            name: "Sprechblase",
            ...enter("pop", { duration: 500 }),
          }),
          {
            kind: "shape",
            shape: "triangle",
            x: 120,
            y: 536,
            w: 70,
            h: 56,
            rotation: 180,
            fill: "background",
            name: "Sprechblase",
            ...enter("pop", { duration: 500 }),
          },
          ellipse({
            x: 100,
            y: 285,
            w: 64,
            h: 64,
            fill: "primary/25",
            name: "Profilbild",
            ...enter("pop", { duration: 500 }),
          }),
          text({
            text: "@lisa.kocht fragt:",
            style: "subline",
            size: 28,
            color: "text-muted",
            x: 184,
            y: 285,
            w: 500,
            h: 64,
            ...enter("pop", { duration: 500 }),
          }),
          logo({
            x: 700,
            y: 287,
            w: 220,
            h: 60,
            on: "background",
            align: "right",
            ...enter("pop", { duration: 500 }),
          }),
          text({
            text: "Wie oft sollte ich eigentlich posten?",
            style: "heading-2",
            size: 58,
            color: "text",
            x: 100,
            y: 372,
            w: 820,
            h: 150,
            ...enter("pop", { duration: 500 }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-podcast",
    title: "Reel: Podcast-Clip",
    description: "Podcast-Titel, Gast als Bauchbinde, pulsierende Tonwelle und Karaoke-Untertitel",
    presetId: "reel",
    topic: "wissen",
    style: "nacht",
    tags: ["podcast", "audio", "interview", "gast", "folge", "talk", "clip", "welle"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 240,
            w: 960,
            h: 210,
            fill: "background/92",
            radius: "lg",
            name: "Podcast",
            ...enter("slide-down"),
          }),
          stack({
            x: 100,
            y: 345,
            w: 640,
            anchor: "center",
            gap: 8,
            items: [
              t("PODCAST · FOLGE 12", "subline", 28, "primary", enter("slide-down")),
              t("Kleine Firma, große Marke", "heading-2", 58, "text", enter("slide-down")),
            ],
          }),
          logo({
            x: 760,
            y: 264,
            w: 220,
            h: 50,
            on: "background",
            align: "right",
            ...enter("slide-down"),
          }),
          rect({
            x: 60,
            y: 480,
            w: 640,
            h: 120,
            fill: "primary",
            radius: "md",
            name: "Gast",
            ...enter("slide-right", { duration: 700 }),
          }),
          stack({
            x: 90,
            y: 540,
            w: 580,
            anchor: "center",
            gap: 4,
            items: [
              t(
                "Zu Gast: Dr. Lena Berger",
                "heading-2",
                40,
                "on:primary",
                enter("slide-right", { duration: 700 }),
              ),
              t(
                "Markenstrategin",
                "caption",
                26,
                "on:primary/80",
                enter("slide-right", { duration: 700 }),
              ),
            ],
          }),
          ...[50, 90, 140, 80, 170, 110, 180, 110, 170, 80, 140, 90, 50].map((height, index) =>
            rect({
              x: 297 + index * 38,
              y: 1000 - height / 2,
              w: 22,
              h: height,
              fill: "accent",
              radius: "full",
              name: "Tonwelle",
              ...enter("zoom", { duration: 300 + index * 60, loop: "pulse" }),
            }),
          ),
          captions({ ...REEL_CAPTIONS, look: "karaoke" }),
        ],
      },
    ],
  },
  {
    id: "reel-news",
    title: "Reel: News & Update",
    description:
      "„NEWS“-Marke mit pulsierendem Punkt, Datum und Schlagzeile – für Vereine und Firmen",
    presetId: "reel",
    topic: "ankuendigung",
    style: "traditionell",
    tags: ["news", "update", "nachricht", "verein", "info", "aktuell", "mitteilung"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 170, 64, "NEWS", "primary", 30, {
            radius: "sm",
            ...enter("slide-right"),
          }),
          ellipse({
            x: 262,
            y: 270,
            w: 24,
            h: 24,
            fill: "primary",
            name: "Live-Punkt",
            ...enter("fade", { loop: "pulse" }),
          }),
          text({
            text: "28. September",
            style: "subline",
            size: 28,
            x: 304,
            y: 250,
            w: 440,
            h: 64,
            ...ON_VIDEO,
            ...enter("fade"),
          }),
          logo({ x: 760, y: 252, w: 250, h: 60, on: BLACK, align: "right" }),
          rect({
            x: 60,
            y: 340,
            w: 960,
            h: 240,
            fill: "background/94",
            radius: "md",
            name: "Schlagzeile",
            ...enter("slide-up", { duration: 600 }),
          }),
          text({
            text: "Neue Trainingszeiten ab Oktober",
            style: "heading-1",
            size: 72,
            color: "text",
            x: 100,
            y: 340,
            w: 880,
            h: 240,
            ...enter("slide-up", { duration: 600 }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-countdown",
    title: "Reel: Countdown zum Launch",
    description: "3 – 2 – 1 auf Farbfläche, danach „Jetzt erhältlich“ über dem Video",
    presetId: "reel",
    topic: "produkt",
    style: "laut",
    tags: ["countdown", "launch", "start", "release", "neu", "eröffnung", "3 2 1", "premiere"],
    previewTime: 400,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(190, 260, 700, 100, "JETZT ERHÄLTLICH", "accent", 40, {
            ...during(3000, 12000, "pop", { duration: 450, loop: "pulse" }),
          }),
          text({
            text: "Die neue Kollektion ist da",
            style: "heading-1",
            size: 100,
            x: 70,
            y: 400,
            w: 940,
            align: "center",
            ...ON_VIDEO,
            ...during(3100, 12000, "slide-up", { duration: 600 }),
          }),
          captions(REEL_CAPTIONS),
          rect({
            x: 0,
            y: 0,
            w: 1080,
            h: 1920,
            fill: "primary",
            name: "Countdown-Fläche",
            ...during(0, 3300, "fade", { duration: 150, outDuration: 300 }),
          }),
          logo({
            x: 340,
            y: 300,
            w: 400,
            h: 110,
            on: "primary",
            align: "center",
            ...during(0, 3000, "fade", { duration: 300 }),
          }),
          ...["3", "2", "1"].map((digit, index) =>
            text({
              text: digit,
              style: "heading-1",
              size: 520,
              color: "on:primary",
              x: 90,
              y: 440,
              w: 900,
              h: 620,
              align: "center",
              name: `Countdown ${digit}`,
              ...during(index * 1000, (index + 1) * 1000, "zoom", {
                duration: 300,
                outDuration: 150,
              }),
            }),
          ),
          text({
            text: "Gleich geht’s los",
            style: "subline",
            size: 44,
            color: "on:primary",
            x: 90,
            y: 1090,
            w: 900,
            align: "center",
            ...during(0, 3000, "fade", { duration: 300 }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-sale",
    title: "Reel: Sale & Rabatt",
    description: "Großes „SALE“, wackelnder Prozent-Stern, Banner mit Frist und Button",
    presetId: "reel",
    topic: "angebot",
    style: "nacht",
    tags: [
      "sale",
      "rabatt",
      "aktion",
      "prozente",
      "black friday",
      "gutschein",
      "deal",
      "schlussverkauf",
    ],
    pages: [
      {
        background: BLACK,
        nodes: [
          logo({ x: 70, y: 236, w: 240, h: 60, on: BLACK }),
          stack({
            x: 70,
            y: 310,
            w: 520,
            gap: 0,
            items: [
              t("SALE", "heading-1", 200, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("zoom", { duration: 500 }),
              }),
              t("auf alles", "heading-2", 56, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("slide-up", { duration: 700 }),
              }),
            ],
          }),
          star({
            x: 590,
            y: 290,
            w: 400,
            h: 400,
            sides: 16,
            innerRatio: 0.8,
            rotation: 10,
            fill: "accent",
            name: "Rabatt",
            ...enter("zoom", { duration: 700, loop: "wiggle" }),
          }),
          text({
            text: "-30\u00a0%",
            style: "heading-1",
            size: 120,
            color: "on:accent",
            x: 640,
            y: 290,
            w: 300,
            h: 400,
            rotation: 10,
            align: "center",
            ...enter("zoom", { duration: 700, loop: "wiggle" }),
          }),
          rect({
            x: -40,
            y: 760,
            w: 1160,
            h: 120,
            fill: "primary",
            rotation: -3,
            name: "Banner",
            ...enter("fly-right", { duration: 700 }),
          }),
          text({
            text: "Nur bis Sonntag, 23:59 Uhr",
            style: "subline",
            size: 46,
            color: "on:primary",
            x: 60,
            y: 760,
            w: 960,
            h: 120,
            rotation: -3,
            align: "center",
            ...enter("fly-right", { duration: 700 }),
          }),
          ...pill(240, 910, 600, 110, "Link in der Bio", "background", 40, {
            ...enter("pop", { duration: 1200, loop: "pulse" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-tutorial-kapitel",
    title: "Reel: Tutorial mit Kapiteln",
    description: "Fortschrittsbalken und Kapitel-Karte für drei Abschnitte, dazu Untertitel",
    presetId: "reel",
    topic: "wissen",
    style: "verspielt",
    tags: [
      "tutorial",
      "anleitung",
      "kapitel",
      "schritt für schritt",
      "erklärung",
      "lernen",
      "how-to",
    ],
    previewTime: 1000,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...parts(3, 0, 6000).flatMap(({ index, start }) => [
            rect({
              x: 70 + index * 320,
              y: 240,
              w: 300,
              h: 12,
              fill: "#ffffff59",
              radius: "full",
              name: "Fortschritt",
            }),
            rect({
              x: 70 + index * 320,
              y: 240,
              w: 300,
              h: 12,
              fill: "primary",
              radius: "full",
              name: `Fortschritt ${index + 1}`,
              ...during(start, 18000, "fade", { duration: 400, out: null }),
            }),
          ]),
          ...parts(3, 0, 6000).flatMap(({ index, start, end }) => {
            const chapters = ["Material vorbereiten", "Zuschnitt & Montage", "Der letzte Schliff"];
            const motion = during(start, end, "slide-right", { duration: 450 });
            return [
              rect({
                x: 60,
                y: 280,
                w: 700,
                h: 170,
                fill: "background/92",
                radius: "lg",
                name: `Kapitel ${index + 1}`,
                ...motion,
              }),
              stack({
                x: 100,
                y: 365,
                w: 620,
                anchor: "center",
                gap: 6,
                items: [
                  t(`KAPITEL ${index + 1} VON 3`, "subline", 26, "primary", motion),
                  t(chapters[index]!, "heading-2", 52, "text", motion),
                ],
              }),
            ];
          }),
          logo({ x: 790, y: 300, w: 220, h: 64, on: BLACK, align: "right" }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-text-oben",
    title: "Reel: Text oben (Meme-Format)",
    description: "Klassischer Textbalken über dem Video – für Humor, Trends und Aha-Momente",
    presetId: "reel",
    topic: "community",
    style: "klar",
    tags: ["meme", "text oben", "humor", "lustig", "trend", "balken", "pov"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({ x: 0, y: 0, w: 1080, h: 640, fill: "background", name: "Textbalken" }),
          text({
            text: "Wenn der Kunde sagt: „Mach das Logo mal größer“",
            style: "heading-2",
            size: 64,
            color: "text",
            x: 70,
            y: 240,
            w: 940,
            h: 360,
            align: "center",
          }),
        ],
      },
    ],
  },
];

export const REELS: LibraryTemplate[] = [...FIRST_REELS, ...MORE_REELS];
